import { useCallback, useEffect, useRef } from "react";

export function useCanvasInteractions({
  canvasRef,
  img,
  scale,
  setScale,
  setOffset,
}) {
  const lastTouchRef = useRef(null);
  const isDraggingRef = useRef(false);

  const pinchStartDist = useRef(null);
  const pinchStartScale = useRef(1);

  const getMinScale = useCallback(() => {
    const canvas = canvasRef.current;

    if (!canvas || !img) return 1;

    return Math.max(
      canvas.width / img.width,
      canvas.height / img.height
    );
  }, [canvasRef, img]);

  const clampScale = useCallback((value) => {
    const minScale = getMinScale();

    return Math.min(5, Math.max(minScale, value));
  }, [getMinScale]);

  const clampOffset = useCallback((offset, nextScale = scale) => {
    const canvas = canvasRef.current;

    if (!canvas || !img) return offset;

    const scaledWidth = img.width * nextScale;
    const scaledHeight = img.height * nextScale;

    const minX = canvas.width - scaledWidth;
    const minY = canvas.height - scaledHeight;

    return {
      x: Math.min(0, Math.max(minX, offset.x)),
      y: Math.min(0, Math.max(minY, offset.y)),
    };
  }, [canvasRef, img, scale]);

  const handleMouseDown = () => {
    isDraggingRef.current = true;
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current || !img) return;

    setOffset((prev) =>
      clampOffset({
        x: prev.x + e.movementX,
        y: prev.y + e.movementY,
      })
    );
  };

  const handleTouchStart = (e) => {
    if (!img) return;

    if (e.touches.length === 1) {
      lastTouchRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }

    if (e.touches.length === 2) {
      const dx =
        e.touches[0].clientX -
        e.touches[1].clientX;

      const dy =
        e.touches[0].clientY -
        e.touches[1].clientY;

      pinchStartDist.current = Math.sqrt(
        dx * dx + dy * dy
      );

      pinchStartScale.current = scale;
    }
  };

  const handleTouchMove = (e) => {
    if (!img) return;

    if (
      e.touches.length === 1 &&
      lastTouchRef.current
    ) {
      const dx =
        e.touches[0].clientX -
        lastTouchRef.current.x;

      const dy =
        e.touches[0].clientY -
        lastTouchRef.current.y;

      lastTouchRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };

      setOffset((prev) =>
        clampOffset({
          x: prev.x + dx,
          y: prev.y + dy,
        })
      );
    }

    if (
      e.touches.length === 2 &&
      pinchStartDist.current
    ) {
      const dx =
        e.touches[0].clientX -
        e.touches[1].clientX;

      const dy =
        e.touches[0].clientY -
        e.touches[1].clientY;

      const dist = Math.sqrt(dx * dx + dy * dy);
      const zoom = dist / pinchStartDist.current;

      const nextScale = clampScale(
        pinchStartScale.current * zoom
      );

      setScale(nextScale);

      setOffset((prev) =>
        clampOffset(prev, nextScale)
      );
    }
  };

  const handleTouchEnd = () => {
    lastTouchRef.current = null;
    pinchStartDist.current = null;
  };

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const wheel = (e) => {
      e.preventDefault();

      if (!img) return;

      const zoomFactor = 1.12;
      const direction = e.deltaY > 0 ? 1 : -1;

      setScale((prev) => {
        const next =
          direction > 0
            ? prev / zoomFactor
            : prev * zoomFactor;

        const nextScale = clampScale(next);

        setOffset((currentOffset) =>
          clampOffset(currentOffset, nextScale)
        );

        return nextScale;
      });
    };

    canvas.addEventListener("wheel", wheel, {
      passive: false,
    });

    return () =>
      canvas.removeEventListener("wheel", wheel);
  }, [canvasRef, img, setScale, setOffset, clampScale, clampOffset]);

  return {
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
}
